// Copy for the dedicated SEO landing pages. Each page is a real page with its own angle,
// examples, FAQs and internal links. Concise editorial style, Australian English, plain language.
// Optional industry sections (problems, customersCare, contentIdeas, caseStudy) render only when present.

export const SERVICE_PAGES = [
  {
    path: "/content-creation-sydney",
    key: "content",
    slot: "service-content",
    nav: "Content Production",
    title: "Content Creation Sydney for Food & Product Businesses",
    description: "On-site content production in Sydney for food manufacturers, distributors, wholesalers and product businesses. One planned shoot, a month of content people actually watch.",
    eyebrow: "Content creation · Sydney",
    h1: ["Content creation in Sydney for businesses that", "actually make things."],
    intro: [
      "Most content agencies want you to come to a studio, stand in front of a white wall and talk about your values. We'd rather come to you.",
      "The factory floor at 5am. The cold room. The packing line. The founder explaining why the recipe changed in 2019. That's where the content is, and it's the content your customers, buyers and future staff want to see.",
    ],
    who: {
      heading: "Who this is for",
      items: [
        "Food manufacturers who know their product is better than the shelf suggests",
        "Distributors and wholesalers whose customers have never seen the warehouse",
        "Packaging, equipment and industrial businesses with a process worth watching",
        "Hospitality and retail owners who are too busy running the floor to film it",
        "Any Sydney business that's been told to \"post more\" without anyone telling them what",
      ],
    },
    how: {
      heading: "How a shoot with us works",
      steps: [
        ["We walk the business first", "Before a camera comes out we spend time on site. We're looking for the things you've stopped noticing: the machine that does something clever, the team member everyone likes, the step that explains why you're more expensive and worth it."],
        ["We plan the month, then film it in a day", "You get a shot list built around real stories, not trends. One well planned day on site usually covers a month of short form, long form and stills without disrupting production."],
        ["We edit for the platform, not just the feed", "Vertical for Reels and TikTok, cut downs for ads, longer pieces for your website and sales conversations. Captions, hooks and formats that suit how each platform is actually watched."],
        ["You approve, we publish or hand over", "Everything lands in one organised place. You can have us manage the channels or take the files and run them yourself."],
      ],
    },
    get: {
      heading: "What you walk away with",
      items: ["A month of short form video from one shoot", "Long form pieces for YouTube, your website and sales", "Product and process photography", "Ad creative ready for Meta campaigns", "Recruitment and culture content your team is proud of", "Website and email assets that match everything else"],
    },
    why: [
      "Angela started full time in her family's food manufacturing business at 16. She has stood on the line, packed the orders and dealt with the retailer. When we film in a factory we already understand what we're looking at, which means we don't get in the way and we don't miss the good bits.",
      "That's the difference between content that looks nice and content that makes a buyer pick up the phone.",
    ],
    faqs: [
      { q: "Do you film in Sydney only?", a: "We're based in Sydney and most shoots happen across Greater Sydney, the Central Coast, Wollongong and the Hunter. We travel further for multi-day shoots. Ask us." },
      { q: "How much disruption is a shoot day?", a: "Very little. We plan around production schedules, film in the gaps and work with whoever is available. Most clients tell us they forgot we were there." },
      { q: "Do we need to be on camera?", a: "Not if you don't want to be. Some of our best performing content has no talking head at all, just the process and a caption. That said, founders on camera build trust quickly, and we make it painless." },
      { q: "Who owns the footage?", a: "You do. Every file we produce for your business belongs to your business." },
    ],
    related: { industries: ["/food-manufacturer-marketing", "/food-distributor-marketing", "/packaging-marketing-agency"], services: ["/social-media-marketing-sydney", "/meta-ads-agency-sydney"] },
  },
  {
    path: "/social-media-marketing-sydney",
    key: "social",
    slot: "service-content",
    nav: "Social Media Marketing",
    title: "Social Media Marketing Sydney for Food & B2B Brands",
    description: "Done-for-you social media marketing in Sydney for food, wholesale and product businesses. Planned, filmed, published and reported, without living on your phone.",
    eyebrow: "Social media marketing · Sydney",
    h1: ["Social media that runs", "while you run the business."],
    intro: [
      "You didn't start a food business to become a social media manager. But the accounts still need to be fed, the enquiries still come through DMs and the buyers still check your page before they call you back.",
      "We plan, film, publish and manage your channels around what's actually happening in the business, so it stays visible and consistent without you thinking about it between deliveries.",
    ],
    who: {
      heading: "Who this is for",
      items: [
        "Owners who post in bursts, then go quiet for six weeks",
        "Businesses where one person holds all the knowledge and none of the time",
        "Wholesalers and distributors who want buyers to see a real, active operation",
        "Grocers, cafés and venues competing with chains on personality, not price",
        "Teams who've tried a freelancer or a junior and ended up doing it themselves anyway",
      ],
    },
    how: {
      heading: "What managing your channels looks like",
      steps: [
        ["A plan built around your calendar", "New product runs, seasonal lines, trade shows, hiring pushes. We map the month to what's really coming up so the content has a job."],
        ["Content from the shoot, not from stock", "Everything we post comes from your business. No stock footage, no generic quote tiles. See how we film on the content creation page."],
        ["Publishing, captions and community", "We write in your voice, schedule at sensible times and reply to comments and messages so leads don't sit unanswered."],
        ["Plain-language reporting", "Once a month you get a short report on what people watched, what they did next and what we're changing. No vanity dashboards."],
      ],
    },
    get: {
      heading: "What's included",
      items: ["Monthly content plan", "Filming and editing from one shoot", "Captions written in your voice", "Scheduling across Instagram, Facebook, TikTok and LinkedIn", "Comment and message handling", "Monthly report with what's next"],
    },
    why: [
      "Because we came from a food business ourselves, we know the rhythm. When the line is down or a big order lands, the plan flexes. The account never goes quiet and nobody has to scramble.",
    ],
    faqs: [
      { q: "Which platforms do you manage?", a: "Instagram, Facebook and TikTok for most food and consumer brands; LinkedIn for wholesalers, manufacturers and B2B. We recommend based on where your customers actually are, not on covering everything." },
      { q: "How often will we post?", a: "Enough to stay present, not so much that quality drops. For most clients that's three to five pieces a week from one monthly shoot, plus stories." },
      { q: "Can you work with content we already have?", a: "Yes. We'll audit what you've got, use what's good and fill the gaps with a shoot." },
      { q: "Is this the same as running ads?", a: "No. Organic social builds familiarity. If you want the strongest pieces in front of more people, that's our paid growth service, and the two work best together." },
    ],
    related: { industries: ["/food-marketing-agency-australia", "/food-wholesaler-marketing"], services: ["/content-creation-sydney", "/meta-ads-agency-sydney"] },
  },
  {
    path: "/meta-ads-agency-sydney",
    key: "paid",
    slot: "service-paid",
    nav: "Paid Growth",
    title: "Meta Ads Agency Sydney for Food & Product Businesses",
    description: "Meta (Facebook and Instagram) advertising for Sydney food, wholesale and product businesses. Real creative from your business, campaigns built to bring in enquiries and orders.",
    eyebrow: "Meta ads · Sydney",
    h1: ["Paid ads that put your best story", "in front of the right people."],
    intro: [
      "Good content earns attention from the people who already follow you. Paid advertising puts it in front of the people who don't yet: the café owner two suburbs over, the category buyer, the family who hasn't found your product on the shelf.",
      "We run Meta campaigns for businesses that sell real products, which means we care about enquiries, orders and repeat customers, not reach.",
    ],
    who: {
      heading: "Who this is for",
      items: [
        "Food brands launching a new line or entering new retailers",
        "Wholesalers and distributors looking for more trade accounts",
        "Local grocers, venues and producers building a customer base in a specific area",
        "Manufacturers who need a steady flow of quotes rather than a spike",
        "Anyone who has boosted posts, spent money and learnt nothing",
      ],
    },
    how: {
      heading: "How we run paid growth",
      steps: [
        ["Offer and audience first", "What are we actually asking someone to do, and who is realistically going to do it? We answer that before a dollar is spent."],
        ["Creative from inside your business", "Our ads use real footage of your process, product and people. It performs better because it looks like nothing else in the feed, and it's already true."],
        ["Launch small, learn fast", "We start with tight budgets and several angles, watch what people respond to, then put the money behind the winners."],
        ["Landing pages and follow up that don't leak", "An ad is only as good as what happens after the click. We build the page and, where needed, the follow up system so leads don't go cold."],
      ],
    },
    get: {
      heading: "What's included",
      items: ["Campaign strategy and audience research", "Ad creative filmed and edited for the platform", "Landing pages that match the ad", "Pixel and conversion tracking set up properly", "Weekly optimisation", "Monthly report in plain language"],
    },
    why: [
      "Plenty of agencies can run Meta ads. Fewer can walk through a warehouse, find the 20 seconds that makes a buyer stop scrolling, film it and turn it into the ad. That combination is the whole point of how we work.",
    ],
    faqs: [
      { q: "What budget do we need?", a: "It depends on the goal and the area. Local campaigns can start modestly; B2B campaigns targeting buyers need more patience than budget. We'll give you a straight answer after a conversation." },
      { q: "Do you run Google Ads too?", a: "Yes, where it makes sense, usually for businesses where people are actively searching for a supplier. Meta is our primary platform for food and consumer brands." },
      { q: "How long before we see results?", a: "Learning happens in the first few weeks. Sustainable results usually take two to three months as creative and audiences settle. We'll show you what's happening the whole way." },
      { q: "Do we own the ad account?", a: "Always. It's your account, your pixel and your data. We just work inside it." },
    ],
    related: { industries: ["/food-marketing-agency-australia", "/food-distributor-marketing"], services: ["/content-creation-sydney", "/website-design-sydney"] },
  },
  {
    path: "/website-design-sydney",
    key: "websites",
    slot: "service-websites",
    nav: "Websites",
    title: "Website Design Sydney for Food, Wholesale & Product Businesses",
    description: "Websites for Sydney food manufacturers, distributors, wholesalers and product businesses. Fast, clear and built around how you actually sell, with enquiry handling that works.",
    eyebrow: "Website design · Sydney",
    h1: ["A website that shows what you're like", "to deal with."],
    intro: [
      "Most business websites are a brochure from five years ago. A buyer lands on it, can't tell what you do or who you do it for, and calls someone else.",
      "We build websites for businesses with real products and real operations. They load fast, they show the business honestly using footage from your own shoot, and they make the next step obvious, whether that's a quote, a wholesale enquiry or an order.",
    ],
    who: {
      heading: "Who this is for",
      items: [
        "Manufacturers and wholesalers whose site doesn't reflect the size of the operation",
        "Food brands that need a trade page and a consumer page that don't fight each other",
        "Businesses getting enquiries by phone that should be arriving as organised leads",
        "Anyone whose current site can't be updated without calling the nephew who built it",
      ],
    },
    how: {
      heading: "How we build",
      steps: [
        ["We map how you sell first", "Who lands on the site, what they need to believe and what they should do next. The structure comes from that, not from a template."],
        ["Real media, not stock", "Your website uses photography and video from your shoot. It looks like your business because it is your business."],
        ["Built for speed and search", "Clean structure, proper headings, fast load times and the technical basics done right so Google can find and rank the pages that matter."],
        ["Connected to how you work", "Enquiries land in your CRM or inbox properly labelled. Tracking is set up for ads. Nothing sits in a form nobody checks."],
      ],
    },
    get: {
      heading: "What's included",
      items: ["Site strategy and structure", "Design and build", "Copy written in your voice", "Enquiry, quote and booking flows", "Analytics and ad tracking", "Training so your team can update it", "Ongoing improvements if you want them"],
    },
    why: [
      "We build the sites, we film the content that goes on them and we run the ads that send people to them. When one team does all three, nothing falls between the gaps.",
    ],
    faqs: [
      { q: "How long does a website take?", a: "A focused site for a product business usually takes four to eight weeks from kick off, depending on content and how quickly decisions get made on your side." },
      { q: "Can you rebuild our existing site?", a: "Yes. We'll keep what's working, fix what isn't and make sure search rankings you've earned aren't lost in the move." },
      { q: "Do you do e-commerce?", a: "For food and product brands, yes, including wholesale ordering portals for trade customers." },
      { q: "Will we be able to edit it ourselves?", a: "Yes. We build so your team can update products, pages and content without calling us. We'll show you how." },
    ],
    related: { industries: ["/food-wholesaler-marketing", "/food-manufacturer-marketing"], services: ["/custom-business-systems", "/meta-ads-agency-sydney"] },
  },
  {
    path: "/custom-business-systems",
    key: "systems",
    slot: "service-systems",
    nav: "Custom Business Systems",
    title: "Custom Business Systems & CRM for Product Businesses",
    description: "Custom CRM, quoting, enquiry handling and client portals for Australian food, wholesale and manufacturing businesses. Systems built around how you actually work so growth doesn't create more admin.",
    eyebrow: "Custom business systems",
    h1: ["Growth shouldn't mean", "more admin."],
    intro: [
      "Marketing that works creates a new problem: more enquiries, more quotes, more follow ups, more things to keep track of. In most product businesses that lands on one person and a spreadsheet.",
      "We build the systems behind the marketing. Enquiry handling, quoting, CRM, client portals and the automations between them, designed around how your team already works rather than forcing you into someone else's software.",
    ],
    who: {
      heading: "Who this is for",
      items: [
        "Businesses where leads arrive by phone, email, DM and form, and nobody has the full picture",
        "Wholesalers managing trade accounts, price lists and reorders by memory",
        "Manufacturers quoting from templates that live in one person's laptop",
        "Owners who want to know what's in the pipeline without asking three people",
      ],
    },
    how: {
      heading: "How we build systems",
      steps: [
        ["We watch how the work really happens", "Not how the process document says it happens. We sit with the people doing it and map the actual steps, workarounds and bottlenecks."],
        ["Design around the business, not the software", "We pick or build the right tools for your size and industry, and shape them to your language, your stages and your reports."],
        ["Connect everything that should talk", "Website forms, ads, email, calendar, accounting. Leads and jobs move without someone re-typing them."],
        ["Train, launch, refine", "Your team learns it properly, we watch how it's used for the first month and adjust. Systems that nobody uses aren't systems."],
      ],
    },
    get: {
      heading: "What we build",
      items: ["Custom CRM and sales pipelines", "Enquiry and lead handling with instant notifications", "Quoting and proposal tools", "Client and trade customer portals", "AI assistants for summaries, drafts and follow ups", "Dashboards the owner actually looks at", "Automations between the tools you already pay for"],
    },
    why: [
      "We use these systems ourselves. The lead centre behind this website, the daily digest, the follow up flows: all built by us, for us, then refined with clients. We know what breaks and what actually gets used.",
    ],
    faqs: [
      { q: "Do we have to change the software we use?", a: "Not necessarily. We often connect and improve what you have. If something genuinely isn't fit for purpose we'll say so and explain why." },
      { q: "Is this only for bigger businesses?", a: "No. Some of the most useful systems we've built are for teams of three to ten where one lost enquiry a week is a real cost." },
      { q: "How is this related to marketing?", a: "Marketing creates the enquiry. The system decides whether it turns into a customer. We build both because we've watched too many good leads die in an inbox." },
      { q: "Can you work with our accountant or bookkeeper's tools?", a: "Usually, yes. Xero, MYOB and most invoicing tools connect well. We'll confirm before we start." },
    ],
    related: { industries: ["/food-wholesaler-marketing", "/food-distributor-marketing"], services: ["/website-design-sydney", "/meta-ads-agency-sydney"] },
  },
];

export const INDUSTRY_PAGES = [
  {
    path: "/food-marketing-agency-australia",
    key: "food",
    slot: "industries",
    nav: "Food Marketing",
    title: "Food Marketing Agency Australia | Viral Vision Media",
    description: "A food marketing agency that grew up inside a food business. Content, paid ads, websites and systems for Australian food manufacturers, distributors, grocers and producers.",
    eyebrow: "Food marketing agency · Australia",
    h1: ["We're a food marketing agency because", "we came from one."],
    intro: [
      "Food is where Viral Vision Media started. Angela worked full time in her family's food manufacturing business from 16, and most of what we believe about marketing comes from watching products get developed, made, sold into retailers and picked up by customers.",
      "So when we talk to a food business, we already understand the margins, the shelf life, the buyer meetings and why Friday afternoons are chaos. We don't need it explained. That lets us get straight to the stories.",
    ],
    who: {
      heading: "Food businesses we work with",
      items: ["Food and beverage manufacturers", "Distributors and cold chain operators", "Wholesalers supplying food service and retail", "Independent grocers and specialty food retailers", "Producers, growers and makers selling direct and wholesale", "Cafés, bakeries, restaurants and venues", "Food safety consultants and laboratories"],
    },
    problems: {
      heading: "The problems we see most",
      items: [
        "The product is excellent but the brand looks smaller online than it is in real life",
        "The retailer or the stockist gets all the credit and all the customer relationship",
        "Marketing happens in bursts around a trade show, then goes quiet for months",
        "Nobody has time to film, so there's nothing to post and nothing for the sales team to send",
      ],
    },
    customersCare: {
      heading: "What your customers actually care about",
      items: [
        "Can you make it consistently, at volume, on time",
        "Is it safe, traceable and made somewhere they'd be happy to show their customers",
        "Who are the people behind it, and would they be easy to deal with",
        "Does it taste, look and sell the way the pitch promised",
      ],
    },
    how: {
      heading: "What food marketing looks like with us",
      steps: [
        ["Show the making", "The production line, the prep, the testing. Customers trust what they can see, and buyers want to know you can deliver at volume."],
        ["Put the people forward", "The founder, the head baker, the driver who's done the same run for 12 years. People buy from people, especially in food."],
        ["Speak to trade and consumer differently", "A category buyer and a home cook need different things. We build content and pages for both without diluting either."],
        ["Back it with paid and systems", "Once the story is working, we amplify it with Meta ads and make sure every trade enquiry is followed up properly."],
      ],
    },
    get: {
      heading: "How we can help",
      items: ["Content production on site", "Social media management", "Meta and Google advertising", "Websites with trade and consumer paths", "Wholesale ordering portals and CRM", "Launch campaigns for new lines and new retailers"],
    },
    contentIdeas: {
      heading: "Content that works for food brands",
      items: ["A day on the production line", "How the recipe was developed", "Meet the maker", "New line launch", "Behind a retailer delivery", "Ingredient sourcing story", "The quality check nobody sees"],
    },
    why: [
      "There are marketing agencies with food clients. There are very few built by people who have worked a production line, argued about a retailer's terms and watched a product they helped make land in someone's trolley. That background is in everything we do.",
    ],
    faqs: [
      { q: "Do you only work with Sydney food businesses?", a: "We're based in Sydney and film across NSW. Strategy, paid advertising, websites and systems work happens with food businesses anywhere in Australia." },
      { q: "We sell wholesale only. Is social media worth it?", a: "Yes, differently. Buyers and their teams are on Instagram and LinkedIn too, and an active, credible presence makes the sales call easier. We'll build for that audience rather than chasing consumer likes." },
      { q: "Can you help with a new product launch?", a: "That's one of our favourite jobs. Content from development through to first delivery, a launch campaign and the follow up system to turn interest into accounts." },
    ],
    related: { services: ["/content-creation-sydney", "/meta-ads-agency-sydney", "/website-design-sydney"], industries: ["/food-manufacturer-marketing", "/food-distributor-marketing", "/food-wholesaler-marketing"] },
  },
  {
    path: "/food-manufacturer-marketing",
    key: "manufacturer",
    slot: "industries",
    nav: "Food Manufacturers",
    title: "Marketing for Food Manufacturers | Viral Vision Media",
    description: "Marketing for Australian food manufacturers: production content, trade and consumer campaigns, websites and CRM from a team that grew up inside a food manufacturing business.",
    eyebrow: "Food manufacturers",
    h1: ["Your production line is the best", "content you're not using."],
    intro: [
      "Food manufacturers are usually invisible online. The product is on shelves, the factory is busy, the website is a logo and a phone number. Meanwhile the brands you supply take all the credit.",
      "We film what actually happens inside the plant, turn it into content buyers and consumers want to watch, and build the marketing around your production reality rather than against it.",
    ],
    who: {
      heading: "Manufacturers we suit",
      items: ["Contract and private label manufacturers who want their own brand presence", "Branded manufacturers selling through retailers and distributors", "Bakeries, sauce makers, snack producers, beverage manufacturers", "Plants moving into new categories or new retailers", "Family businesses handing over to the next generation"],
    },
    problems: {
      heading: "The problems we see most",
      items: [
        "Buyers can't tell you apart from three other suppliers on price alone",
        "All the hard-won expertise lives in people's heads, not anywhere a customer can see it",
        "Private label work pays the bills but there's no brand of your own to grow",
        "The sales team has nothing modern to send between meetings",
      ],
    },
    customersCare: {
      heading: "What buyers need to believe",
      items: [
        "You can scale without quality slipping",
        "Your food safety and traceability are genuinely sorted",
        "You'll still be reliable when their order doubles",
        "The people running the plant know what they're doing",
      ],
    },
    how: {
      heading: "What we do for food manufacturers",
      steps: [
        ["Film production without slowing it", "We understand HACCP, hairnets and why we can't stand there. We plan the shoot with your production manager and stay out of the way."],
        ["Make quality visible", "Testing, traceability, consistency. The things that win a buyer's confidence are usually the things you never show anyone."],
        ["Give the sales team something to send", "Short videos and one pagers that make the next buyer meeting easier, plus a website that backs up the pitch."],
        ["Keep every trade enquiry moving", "A CRM built for how manufacturers actually sell: long cycles, samples, spec sheets and follow ups that don't fall through."],
      ],
    },
    get: {
      heading: "Typical work",
      items: ["Production and process video", "Founder and team stories", "Trade focused website with capability pages", "LinkedIn and Instagram management", "Meta campaigns for consumer lines", "Quoting and enquiry systems"],
    },
    contentIdeas: {
      heading: "Content that works for manufacturers",
      items: ["Inside the plant", "From raw material to finished product", "A quality check explained", "Meet the production team", "How we handle a big order", "Capability tour for buyers"],
    },
    why: [
      "Angela has been on the manufacturer's side of a retailer negotiation. We know what a buyer needs to see to feel safe choosing you, and we know how to show it without giving away the recipe.",
    ],
    faqs: [
      { q: "Our facility is restricted. Can you still film?", a: "Yes. We follow your food safety requirements, work with your QA team on what can and can't be shown, and can sign NDAs. Plenty can be filmed without revealing anything proprietary." },
      { q: "We're private label. Do we even need marketing?", a: "If you want better clients, better terms or your own brand one day, yes. Buyers research suppliers like anyone else, and a credible presence shortens the sales cycle." },
    ],
    related: { services: ["/content-creation-sydney", "/website-design-sydney", "/custom-business-systems"], industries: ["/food-marketing-agency-australia", "/manufacturing-marketing-agency", "/packaging-marketing-agency"] },
  },
  {
    path: "/food-distributor-marketing",
    key: "distributor",
    slot: "industries",
    nav: "Food Distributors",
    title: "Marketing for Food Distributors | Viral Vision Media",
    description: "Marketing for food distributors and cold chain businesses in Australia. Turn the warehouse, the fleet and the early starts into content that wins accounts and keeps them.",
    eyebrow: "Food distributors",
    h1: ["Turn the warehouse", "into the story."],
    intro: [
      "Distribution is a trust business. A café owner or a grocer is choosing who turns up at 6am with the right stock, cold, on time, every time. Almost none of that is visible online.",
      "We show it. The warehouse, the picking, the fleet, the people who know every customer's order by heart. Then we put it in front of the venues and retailers you want and make it easy for them to become an account.",
    ],
    who: {
      heading: "Distributors we work with",
      items: ["Food service distributors supplying cafés, restaurants and venues", "Cold chain and fresh produce distributors", "Specialty and imported food distributors", "Beverage distributors", "Regional distributors expanding into new areas"],
    },
    problems: {
      heading: "The problems we see most",
      items: [
        "New venues don't know you exist until a rep happens to call",
        "Reliability is your biggest selling point and it's completely invisible online",
        "Range changes and new arrivals never reach existing accounts in time",
        "Onboarding a new account takes weeks of back-and-forth",
      ],
    },
    customersCare: {
      heading: "What a chef or grocer is deciding",
      items: [
        "Will you actually turn up on time, cold, with the right order",
        "Can you cover them when something runs out",
        "Is the range worth switching suppliers for",
        "Are you easy to deal with when something goes wrong",
      ],
    },
    how: {
      heading: "What we do for distributors",
      steps: [
        ["Content from the operation", "Early starts, loading, delivery runs, new product arrivals. This is the content chefs and grocers stop for, because it's their world too."],
        ["Show the range without a catalogue", "Short product features, supplier stories and seasonal arrivals that keep existing accounts ordering more."],
        ["Target venues by area", "Meta campaigns aimed at the suburbs and business types you deliver to, sending them to a trade page that makes enquiring simple."],
        ["Handle trade enquiries properly", "An ordering portal, a CRM and follow up flows so a new account is set up in days, not weeks."],
      ],
    },
    get: {
      heading: "Typical work",
      items: ["Warehouse and fleet video", "Product arrival and range content", "Trade website with ordering", "Instagram and LinkedIn management", "Local Meta campaigns", "Account onboarding systems"],
    },
    contentIdeas: {
      heading: "Content that works for distributors",
      items: ["The 4am run", "New arrivals this week", "Meet your driver", "Inside the cold room", "A supplier spotlight", "How an order gets picked"],
    },
    why: [
      "We've stood in the cold room at 4am with a camera and a coffee. We know the delivery run isn't glamorous, and we know exactly why it's the most convincing thing you can show a new customer.",
    ],
    faqs: [
      { q: "Our customers are chefs. Are they really on social media?", a: "Very much so, and mostly on Instagram. They follow suppliers, other kitchens and producers. Showing up consistently in that feed makes the first call warmer." },
      { q: "Can you help us grow into a new region?", a: "Yes. Area targeted campaigns plus content that shows you already service similar businesses is a strong combination for territory expansion." },
    ],
    related: { services: ["/content-creation-sydney", "/meta-ads-agency-sydney", "/custom-business-systems"], industries: ["/food-wholesaler-marketing", "/food-marketing-agency-australia"] },
  },
  {
    path: "/food-wholesaler-marketing",
    key: "wholesale",
    slot: "industries",
    nav: "Food Wholesalers",
    title: "Food Wholesaler Marketing Agency | Viral Vision Media",
    description: "Marketing for food wholesalers in Australia: B2B content, trade websites and ordering portals, LinkedIn and Meta campaigns, and CRM built for account based selling.",
    eyebrow: "Food wholesalers",
    h1: ["Marketing for food wholesalers who sell on", "relationships, not discounts."],
    intro: [
      "Food wholesale is won on range, reliability and the person on the other end of the phone. The problem is that a new buyer can't see any of that from your website, and your reps can only be in one place at a time.",
      "We build the marketing that does the introduction for them: content that shows a real, well run operation, a trade site that answers the buyer's questions and campaigns that reach the accounts you actually want.",
    ],
    who: {
      heading: "Food wholesalers we suit",
      items: ["Food and beverage wholesalers", "Ingredient and raw material suppliers", "Specialty and imported food wholesalers", "Wholesalers with both trade and direct to consumer arms", "Family wholesale businesses modernising how they sell"],
    },
    problems: {
      heading: "The problems we see most",
      items: [
        "Everything depends on reps, and reps can only hold so many relationships",
        "A strong range and reliable supply never show up anywhere a buyer can find them",
        "Price lists, minimums and reorders are managed from memory and spreadsheets",
        "The business looks dated online next to competitors who've invested in it",
      ],
    },
    customersCare: {
      heading: "What a trade buyer is weighing up",
      items: [
        "Is the range deep enough to make you their main supplier",
        "Will supply stay reliable when they scale",
        "Are the terms, minimums and ordering process easy to live with",
        "Can they trust you over the supplier they already use",
      ],
    },
    how: {
      heading: "What we do for food wholesalers",
      steps: [
        ["Make the operation visible", "Warehouse, range, people, systems. The things that make a buyer feel safe placing a first order."],
        ["Build a trade site that works like a good rep", "Clear categories, capability pages, easy quote and account requests, and an ordering portal for existing customers."],
        ["Reach buyers where they are", "LinkedIn for decision makers, Meta for owner operators, targeted by industry and area."],
        ["Systematise the follow up", "A CRM for account based selling: samples, price lists, reorder reminders and the history behind every relationship."],
      ],
    },
    get: {
      heading: "Typical work",
      items: ["B2B content production", "Trade website and ordering portal", "LinkedIn and Instagram management", "Targeted Meta and LinkedIn campaigns", "CRM and account management systems", "Sales collateral that matches the brand"],
    },
    contentIdeas: {
      heading: "Content that works for wholesalers",
      items: ["Inside the warehouse", "The range, category by category", "Meet the account team", "A new supplier or line", "How ordering works", "Why accounts stay with you"],
    },
    why: [
      "Angela's family business sold wholesale into major retailers. We understand terms, minimums, credit applications and the long game of keeping an account. That shapes how we market wholesalers.",
    ],
    faqs: [
      { q: "Is social media really relevant for B2B food wholesale?", a: "Yes, when it's aimed at the right people. Buyers research suppliers. An active, credible presence backs up your reps and keeps you in mind between orders." },
      { q: "We have reps. Won't this replace them?", a: "No. It makes their job easier. Warmer introductions, better collateral and a CRM that remembers what they don't." },
    ],
    related: { services: ["/website-design-sydney", "/custom-business-systems", "/content-creation-sydney"], industries: ["/food-distributor-marketing", "/packaging-marketing-agency"] },
  },
  {
    path: "/packaging-marketing-agency",
    key: "packaging",
    slot: "industries",
    nav: "Packaging Manufacturers",
    title: "Packaging Marketing Agency Australia | Viral Vision Media",
    description: "A packaging marketing agency for Australian packaging manufacturers and suppliers. Turn technical knowledge into content buyers care about, with capability websites and lead systems built for long sales cycles.",
    eyebrow: "Packaging marketing agency",
    h1: ["Technical knowledge is only valuable if", "buyers can see it."],
    intro: [
      "Packaging businesses know an enormous amount: materials, compliance, shelf life, print, sustainability, machinery. Very little of it makes it past the sales team and onto anything a buyer can find on their own.",
      "We turn that knowledge into content, pages and campaigns that help a food brand, manufacturer or procurement manager understand why you're the safer choice, long before they call.",
    ],
    who: {
      heading: "Packaging businesses we suit",
      items: ["Flexible and rigid packaging manufacturers", "Printed packaging and label producers", "Sustainable and compostable packaging suppliers", "Packaging wholesalers and distributors", "Custom and short-run packaging makers"],
    },
    problems: {
      heading: "The problems we see most",
      items: [
        "Brilliant custom work is done, then never shown to anyone outside the client",
        "The business relies almost entirely on referrals and is invisible in search",
        "Deep technical expertise never becomes content a buyer can find",
        "Long sales cycles go cold because nothing keeps you present between quotes",
      ],
    },
    customersCare: {
      heading: "What a packaging buyer is deciding",
      items: [
        "Can you deliver the spec, the print quality and the compliance they need",
        "Will lead times and minimums fit how they actually order",
        "Are the sustainability claims real and specific",
        "Have you solved a problem like theirs before",
      ],
    },
    how: {
      heading: "What we do for packaging businesses",
      steps: [
        ["Film the machinery and the craft", "Presses running, materials being tested, samples being cut. Manufacturing footage is compelling and almost nobody in packaging uses it."],
        ["Explain the technical simply", "Short pieces on materials, compliance and sustainability that answer the questions your reps get asked every week."],
        ["Build a capability website", "Structured by what buyers search for, with case examples, specs and an easy path to a sample or quote."],
        ["Support a long sales cycle", "CRM and nurture flows that keep you present across the months between first enquiry and first order."],
      ],
    },
    get: {
      heading: "Typical work",
      items: ["Manufacturing and process video", "Explainer content for technical topics", "Capability website with quote flows", "LinkedIn management and campaigns", "Sales collateral and case studies", "CRM for long cycle B2B sales"],
    },
    contentIdeas: {
      heading: "Content that works for packaging",
      items: ["A custom job, start to finish", "Materials explained simply", "Inside the print run", "Sustainability, honestly", "Before and after a redesign", "Meet the team behind the spec"],
    },
    caseStudy: {
      to: "/work/gorilla-packaging",
      label: "Gorilla Packaging",
      blurb: "Invisible online and relying on referrals, Gorilla Packaging picked up 10 Instagram leads within the first few posts of an organic content strategy built around their real client work.",
    },
    why: [
      "We've worked on the food side of the packaging conversation, choosing suppliers and living with the results. We know what a food brand is worried about when they pick a packaging partner, which is exactly what your marketing should address.",
    ],
    faqs: [
      { q: "Our buyers are procurement teams. Does content reach them?", a: "It reaches the people who influence them: brand managers, operations leads, founders. And procurement teams do their own research too. Being the supplier with clear, useful content makes the shortlist easier." },
      { q: "Can you help us talk about sustainability without greenwashing?", a: "Yes. We stick to what's true and specific about your materials and processes, which buyers trust far more than broad claims." },
    ],
    related: { services: ["/content-creation-sydney", "/website-design-sydney", "/custom-business-systems"], industries: ["/food-manufacturer-marketing", "/food-wholesaler-marketing"] },
  },
  {
    path: "/food-equipment-marketing",
    key: "equipment",
    slot: "industries",
    nav: "Food Equipment Suppliers",
    title: "Food Equipment Marketing Agency | Viral Vision Media",
    description: "Marketing for food equipment, food machinery and commercial kitchen equipment suppliers in Australia. Show the machines doing the work, with capability sites and lead systems for long sales cycles.",
    eyebrow: "Food & kitchen equipment",
    h1: ["Let the machine do", "the selling."],
    intro: [
      "Food machinery and commercial kitchen equipment sells itself in person. The buyer watches it run, sees the output, understands the payback. Online, almost none of that comes through, so it comes down to a spec sheet and a price.",
      "We film the equipment doing exactly what it's built to do, explain the numbers that matter and build the marketing around how these decisions are really made: slowly, by several people, with real money on the line.",
    ],
    who: {
      heading: "Equipment businesses we suit",
      items: ["Food processing and packaging machinery suppliers", "Commercial kitchen and hospitality equipment suppliers", "Refrigeration and cold chain equipment", "Importers and distributors of specialist equipment", "Service, installation and maintenance businesses"],
    },
    problems: {
      heading: "The problems we see most",
      items: [
        "A machine that's genuinely impressive in person looks like a catalogue photo online",
        "Buyers compare on price because the value and output aren't shown anywhere",
        "The sales cycle is long and leads go quiet with nothing keeping you in front of them",
        "Service and support, a real differentiator, is never part of the story",
      ],
    },
    customersCare: {
      heading: "What an equipment buyer is weighing up",
      items: [
        "What it actually produces, how fast, and the payback",
        "How much labour, waste or downtime it removes",
        "What installation, training and support look like",
        "Whether you'll still be there when it needs servicing",
      ],
    },
    how: {
      heading: "What we do for equipment suppliers",
      steps: [
        ["Film the machine in action", "Running at line speed, with real product, next to the output. Watching it work does more than any spec sheet."],
        ["Translate specs into outcomes", "Throughput, labour saved, waste reduced, payback period. We turn the numbers buyers care about into content they can follow."],
        ["Build a capability and range site", "Organised by what buyers search for, with demo videos, specs and a clear path to a quote or site visit."],
        ["Keep long cycles warm", "Follow up flows and a CRM so a six-month decision doesn't go cold between the demo and the purchase order."],
      ],
    },
    get: {
      heading: "Typical work",
      items: ["Equipment demonstration video", "Specs-to-outcomes explainer content", "Range and capability website", "LinkedIn and Meta campaigns", "Installation and service stories", "Quoting and lead nurture systems"],
    },
    contentIdeas: {
      heading: "Content that works for equipment",
      items: ["The machine at full speed", "Payback in plain numbers", "Install and training day", "Before and after automation", "A service call done right", "Buyer's guide to choosing"],
    },
    why: [
      "From the food manufacturing side, we've been the buyer evaluating equipment: weighing output, labour and whether support would actually show up. We know what a serious buyer needs to see, and most equipment marketing never shows it.",
    ],
    faqs: [
      { q: "Can you film on a customer's site?", a: "Yes, with their permission. Real installations running in a real kitchen or plant are some of the most persuasive content you can have, and we handle it discreetly." },
      { q: "Our range is huge. Where do we start?", a: "With the equipment that wins you the best customers and the best margins. We build around your flagship lines first, then expand." },
    ],
    related: { services: ["/content-creation-sydney", "/website-design-sydney", "/meta-ads-agency-sydney"], industries: ["/food-manufacturer-marketing", "/manufacturing-marketing-agency"] },
  },
  {
    path: "/manufacturing-marketing-agency",
    key: "manufacturing",
    slot: "industries",
    nav: "Manufacturers",
    title: "Manufacturing Marketing Agency Australia | Viral Vision Media",
    description: "A manufacturing marketing agency for Australian manufacturers beyond food. Turn the factory floor into content, build capability websites and lead systems for long B2B sales cycles.",
    eyebrow: "Manufacturing marketing agency",
    h1: ["The factory floor is the most convincing", "thing you own."],
    intro: [
      "We grew up marketing food manufacturers, and the same truth holds across manufacturing: the business is impressive in person and invisible online. The capability is real, the people are skilled, and none of it reaches the buyer until a rep gets in the room.",
      "We film the floor, make your capability obvious and build the marketing around how industrial buyers actually decide, so you're on the shortlist before the first call.",
    ],
    who: {
      heading: "Manufacturers we suit",
      items: ["Contract and custom manufacturers", "Industrial and component manufacturers", "Plastics, metal and materials processors", "Fabrication and engineering businesses", "Manufacturers selling to other businesses on long cycles"],
    },
    problems: {
      heading: "The problems we see most",
      items: [
        "Decades of capability sit behind a website that's barely changed in years",
        "Buyers can't gauge your scale, quality or fit without a meeting",
        "Everything rides on referrals and a couple of long-standing accounts",
        "Skilled roles are hard to fill because nobody outside knows what you do",
      ],
    },
    customersCare: {
      heading: "What an industrial buyer needs to believe",
      items: [
        "You can hold tolerance, quality and consistency at volume",
        "Your capacity and lead times fit their project",
        "You've handled work like theirs before",
        "You'll communicate and deliver without being chased",
      ],
    },
    how: {
      heading: "What we do for manufacturers",
      steps: [
        ["Film the floor and the capability", "Machines, processes, finished parts and the people who make them. Capability you can see beats capability you have to describe."],
        ["Make quality and capacity legible", "Tolerances, certifications, throughput and the systems behind them, explained for the people who specify and the people who sign off."],
        ["Build a capability website", "Structured around what buyers search for, with process pages, case examples and a clear path to an enquiry or RFQ."],
        ["Support long B2B cycles", "A CRM and follow up flows for quotes, samples and specifications so slow decisions don't become lost ones."],
      ],
    },
    get: {
      heading: "Typical work",
      items: ["Factory and process video", "Capability and process explainer content", "Capability website with RFQ flows", "LinkedIn management and campaigns", "Recruitment and culture content", "CRM for long cycle B2B sales"],
    },
    contentIdeas: {
      heading: "Content that works for manufacturers",
      items: ["A part from raw material to finished", "Inside the process", "Meet the people on the floor", "A tricky job solved", "Quality and certification explained", "Why customers stay"],
    },
    why: [
      "Our background is food manufacturing, but the instinct transfers: find the 20 seconds on the floor that makes a buyer trust you, and show it. Manufacturing is full of those moments and almost nobody films them.",
    ],
    faqs: [
      { q: "We're not food. Are you still the right fit?", a: "Yes. Food manufacturing is where we started, but the approach, making capability visible and supporting long B2B sales, works across manufacturing. We'll be upfront if your sector needs something we can't do well." },
      { q: "Can this help us hire as well as sell?", a: "Absolutely. Showing what it's really like on the floor is one of the best recruitment tools there is, and it's the same footage that wins customers." },
    ],
    related: { services: ["/content-creation-sydney", "/website-design-sydney", "/custom-business-systems"], industries: ["/food-manufacturer-marketing", "/food-equipment-marketing", "/packaging-marketing-agency"] },
  },
];

export const ALL_LANDINGS = [...SERVICE_PAGES, ...INDUSTRY_PAGES];
export const findLanding = (path) => ALL_LANDINGS.find((p) => p.path === path);
